import { HttpClient } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { FileItem, FileUploader } from 'ng2-file-upload';
import {
    DefaultDataService,
    HttpUrlGenerator,
    Logger,
    QueryParams,
    Update,
} from 'ngrx-data';
import { merge, Observable, Subject } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { mapProperties, mapPropertiesObj } from '../general/generic.function';
import { I1FilenetServices } from '../i1-filenet-services.service';
import { Document } from './document.dto';

@Injectable({
    providedIn: 'root',
})
export class DocumentDataService extends DefaultDataService<Document> {
    private uploader: FileUploader = new FileUploader({
        url: environment.contractFormURL,
    });
    private completed$ = new Subject<Document>();
    private progress$ = new Subject<any>();
    public failed$ = new Subject<any>();

    private workingDocument: Document;

    update(input: Update<Document>): Observable<Document> {
        console.log('Updating');
        const document = ({ ...input.changes } as unknown) as Document;
        const updateDoc = mapPropertiesObj({ ...document }) as Document;
        updateDoc.alerts = updateDoc.alerts
            ? updateDoc.alerts.map(v =>
                  mapPropertiesObj({
                      ...v,
                  })
              )
            : [];
        updateDoc.annotations = updateDoc.annotations
            ? updateDoc.annotations.map(v => mapPropertiesObj({ ...v }))
            : [];

        delete updateDoc.audits; // We dont ever update audits

        const os = updateDoc.objectStore;

        delete updateDoc.objectStore; // Not accepted by backend

        return this.i1FilenetServices.updateDocument(os, updateDoc).pipe(
            filter(v => !!v),
            map(v => {
                const result = (mapProperties(v[0]) as unknown) as Document;
                if (result.annotations) {
                    result.annotations = result.annotations.map(row =>
                        mapProperties(row)
                    );
                }
                if (result.alerts) {
                    result.alerts = result.alerts
                        .map(v => {
                            v.id = v.id.startsWith('TEMP') ? undefined : v.id;
                            return v;
                        })
                        .map(row => mapProperties(row));
                }

                if (result.audits) {
                    result.audits = result.audits
                        .map(row => mapProperties(row))
                        .sort((a, b) =>
                            a.propertiesObj.DateCreated.getTime() >
                            b.propertiesObj.DateCreated.getTime()
                                ? -1
                                : 1
                        );
                }
                // if(result.ann)
                return result;
            })
        );
    }

    add(document: Document): Observable<Document> {
        const uploadDoc = mapPropertiesObj({ ...document });
        uploadDoc.alerts = uploadDoc.alerts
            ? uploadDoc.alerts.map(v =>
                  mapPropertiesObj({
                      ...v,
                  })
              )
            : [];
        uploadDoc.annotations = uploadDoc.annotations
            ? uploadDoc.annotations.map(v => mapPropertiesObj({ ...v }))
            : [];

        this.uploader.clearQueue();

        this.workingDocument = uploadDoc;
        this.uploader.addToQueue([document.file.fileObject]);
        // console.log('Uploader status', this.uploader);
        this.uploader.uploadAll();

        return merge(
            this.completed$.asObservable(),
            this.failed$.asObservable()
        );
    }

    constructor(
        http: HttpClient,
        httpUserGenerator: HttpUrlGenerator,
        @Optional() logger: Logger,
        private i1FilenetServices: I1FilenetServices
    ) {
        super('Document', http, httpUserGenerator);

        this.uploader.onProgressItem = (file: FileItem, progress) =>
            this.progress$.next(progress);

        this.uploader.onCompleteItem = (
            item: FileItem,
            response: string,
            status: number
        ) => {
            if (status === 200) {
                const resultRaw = JSON.parse(response) as Document[];

                const result = (mapProperties(
                    resultRaw[0]
                ) as unknown) as Document;
                if (result.annotations) {
                    result.annotations = result.annotations.map(row =>
                        mapProperties(row)
                    );
                }
                if (result.alerts) {
                    result.alerts = result.alerts.map(row =>
                        mapProperties(row)
                    );
                }

                if (result.audits) {
                    result.audits = result.audits.map(row =>
                        mapProperties(row)
                    );
                }

                this.completed$.next(result);
            } else {
                this.failed$.next({ Message: response, ResponseCode: status });
            }
        };

        this.uploader.onErrorItem = (
            item: FileItem,
            response: string,
            status: number
        ) => this.failed$.next({ Message: response, ResponseCode: status });

        this.uploader.onBuildItemForm = (fileItem: any, form: FormData) => {
            try {
                form.append('document', JSON.stringify(this.workingDocument));
            } catch (e) {
                console.error('Error building Form', e);
                throw e;
            }
        };
    }

    getDuplicates(
        objectStore: string,
        hash: string,
        size: string,
        title: string,
        path: string
    ): Observable<Document[]> {
        return this.i1FilenetServices.getDuplicateDocument(
            objectStore,
            hash,
            size,
            title,
            path
        );
    }

    getWithQuery(queryParams: QueryParams): Observable<Document[]> {
        // console.log('Get With Query: ' + queryParams);
        const os = queryParams.objectStore as string;
        const duplicateCheck = queryParams.duplicateCheck as string;

        let restCall: Observable<Document[]>;
        if (duplicateCheck === 'true') {
            const title = queryParams.title as string;
            const hash = queryParams.hash as string;
            const size = queryParams.size as string;
            const path = queryParams.path as string;
            restCall = this.getDuplicates(os, hash, size, title, path);
        } else {
            const documentIDs = queryParams.DocumentIds as string[];
            const complete = queryParams.complete as string;
            const searchResults = queryParams.searchResults as string;

            restCall = (this.i1FilenetServices.getDocuments(
                os,
                documentIDs,
                complete,
                searchResults
            ) as unknown) as Observable<Document[]>;
        }
        return restCall.pipe(
            map(documents => {
                return documents.map(document => {
                    const result = (mapProperties(
                        document
                    ) as unknown) as Document;
                    if (result.annotations) {
                        result.annotations = result.annotations.map(row =>
                            mapProperties(row)
                        );
                    }
                    if (result.alerts) {
                        result.alerts = result.alerts.map(row =>
                            mapProperties(row)
                        );
                    }
                    if (result.audits) {
                        result.audits = result.audits
                            .map(row => mapProperties(row))
                            .sort((a, b) =>
                                a.propertiesObj.DateCreated.getTime() >
                                b.propertiesObj.DateCreated.getTime()
                                    ? -1
                                    : 1
                            );
                    }
                    return result;
                });
            })
        );
    }
}
